<template>
  <div class="account-area">
    <div class="account-area__sidebar">
      <div class="account-area__information">
        <h4 class="mb-1 text-lg">
          {{ user?.data?.first_name }} {{ user?.data?.last_name }}
        </h4>
        <div class="text-sm font-normal text-gray-500">{{ getBalance }}</div>
      </div>
      <div class="account-area__navigation">
        <NuxtLink
          v-for="item in sidebarNavigation"
          :key="item.path"
          :to="$localePath(item.path)"
          class="link"
          :class="{
            'link--highlighted': item.isHighlighted,
          }"
        >
          <component :is="item.icon" />
          <span>{{ item.title }}</span>
        </NuxtLink>
      </div>
    </div>

    <div class="account-area__content">
      <div v-if="$slots.title" class="account-area__title">
        <slot name="title"></slot>
      </div>
      <div v-if="$slots.subtitle" class="account-area__subtitle">
        <slot name="subtitle"></slot>
      </div>

      <div class="account-area__inner">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLocalePath } from '#imports'
import { formatPriceAmount } from '~/utils/currencyHelpers'

// icons
import AccountIcon1 from '~/assets/account/account-icon-1.svg?component'
import AccountIcon2 from '~/assets/account/account-icon-2.svg?component'
import AccountIcon3 from '~/assets/account/account-icon-3.svg?component'
import AccountIcon4 from '~/assets/account/account-icon-4.svg?component'

const $localePath = useLocalePath()
const { t, locale } = useI18n()
const { user } = useAccount()

// account navigation links
const sidebarNavigation = [
  {
    title: t('account.generalInfo.label'),
    path: '/account/',
    icon: AccountIcon1,
  },
  {
    title: t('account.orders.label'),
    path: '/account/orders',
    icon: AccountIcon2,
  },
  {
    title: t('account.userInfo.label'),
    path: '/account/user-info',
    icon: AccountIcon3,
  },
  {
    title: t('account.referral.label'),
    path: '/account/referral-info',
    icon: AccountIcon4,
    isHighlighted: true,
  },
]

const getBalance = computed(() => {
  const balance = user.value?.data?.balance?.amount || 0

  return `
    ${formatPriceAmount(balance, locale.value)} ${t('account.balance')}
  `
})
</script>

<style lang="postcss" scoped>
.account-area {
  @apply flex flex-col md:flex-row gap-6 pt-8;

  &--loading {
    @apply animate-pulse;
  }

  &__sidebar {
    @apply w-full md:w-2/6;
  }

  &__information {
    @apply w-full p-6 rounded-lg;
    box-shadow: 0 0 20px 0 rgba(76, 87, 125, 0.06);
  }

  &__navigation {
    @apply flex flex-col gap-2 mt-6;

    .link {
      @apply px-4 py-2.5 font-medium rounded-lg text-teal-900 hover:bg-gray-200 duration-300 ease-in-out flex items-center gap-2.5;

      &--highlighted {
        @apply bg-[#faffee];
      }

      &.router-link-exact-active {
        @apply bg-teal-50;
      }
    }
  }

  &__content {
    @apply w-full md:w-4/6 rounded-lg;
  }

  &__title {
    @apply mb-6 text-lg font-semibold;
    &:has(+ .account-area__subtitle) {
      @apply mb-1;
    }
  }

  &__subtitle {
    @apply font-normal text-gray-500 text-sm mb-6;
  }
}
</style>
